import { type LayoutProps, type SpaceProps } from "@chakra-ui/react";
import React, { type ReactNode } from "react";
import { MotionBox } from "./chakra-motion";

type AnimationProps = {
  /**
   * Animation duration
   *
   * @default 0.6
   */
  duration?: number;

  /**
   * Animation delay
   *
   * @default 0
   */
  delay?: number;

  /**
   * Determines when the animation should trigger, based on how much of the element is in the viewport
   * Avoid using `1`, as it can have issues in Safari. Prefer `0.9` or `0.95` instead.
   *
   * @default "some"
   */
  viewportAmount?: "some" | number;

  /**
   * The children to animate
   */
  children: ReactNode;
} & LayoutProps &
  SpaceProps;

export function FadeUpwards({
  duration = 0.6,
  delay = 0,
  viewportAmount = "some",
  children,
  ...props
}: AnimationProps) {
  return (
    <MotionBox
      pos="relative"
      zIndex={1}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0, transition: { type: "easeOut", duration, delay } }}
      viewport={{ once: true, amount: viewportAmount }}
      {...props}
    >
      {children}
    </MotionBox>
  );
}

export function FadeIn({ duration = 0.6, delay = 0, viewportAmount = "some", children, ...props }: AnimationProps) {
  return (
    <MotionBox
      pos="relative"
      zIndex={1}
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1, transition: { type: "easeOut", duration, delay } }}
      viewport={{ once: true, amount: viewportAmount }}
      {...props}
    >
      {children}
    </MotionBox>
  );
}

export function BlurFadeIn({ duration = 0.6, delay = 0, viewportAmount = "some", children, ...props }: AnimationProps) {
  return (
    <MotionBox
      pos="relative"
      zIndex={1}
      initial={{ opacity: 0, filter: "blur(10px)" }}
      whileInView={{ opacity: 1, filter: "blur(0px)", transition: { type: "easeOut", duration, delay } }}
      viewport={{ once: true, amount: viewportAmount }}
      {...props}
    >
      {children}
    </MotionBox>
  );
}
